/**
 * Escherllate tile metadata carried as QR-code pixel data in named PSD layers
 * (brief 07-metadata-bug.md). PSD-level metadata (XMP) does NOT survive a
 * Procreate round-trip; named layers + their pixels do (the whole paint-layer
 * scheme relies on it), so the metadata rides as an image instead.
 *
 * This module is the pure, DOM-free core: schema (de)serialisation, QR encode
 * to a boolean module matrix, a matrix→RGBA renderer, QR decode from RGBA, and
 * the import-time validation + sanity cross-check. Canvas drawing (export) and
 * layer-pixel reading (import) live in psdExport.ts / psdImport.ts; everything
 * here works on plain data so it unit-tests in the node env with no DOM.
 */
import { create as createQr } from 'qrcode';
import jsQR from 'jsqr';
import {
  WALLPAPER_GROUPS,
  colourCountsFor,
  type ArtistTile,
  type LatticeParams,
  type WallpaperGroupId,
} from '@/patterns/wallpaper';

/** Everything needed to reconstruct + tile the artwork from the PSD alone. */
export interface EscherllateData {
  v: 1;
  /** opaque id (sketch name or generated) — display/sanity only, not load-bearing */
  tileId: string;
  wpgId: WallpaperGroupId;
  latticeParams: LatticeParams;
  artistTile: ArtistTile;
  colourCount: number;
  /** trace/region names — cross-checked against the file's Paint- layers */
  regions: string[];
  /** expected document dimensions — cross-checked against the file */
  canvasW: number;
  canvasH: number;
  /**
   * Pixel rect of tile-space [0,1]² within the exported canvas — its (0,0) and
   * (1,1) corners as `[x0, y0, x1, y1]`. The export canvas is a padded, non-square
   * region (trace bbox ∪ unit cell + margin, plus a reserved QR strip below), so
   * the unit cell is only a SUB-RECT of it. The importer crops the composited
   * paint to this rect before scaling it to the tile raster, so the tiled bitmap
   * faithfully represents the unit cell rather than the whole padded canvas
   * (fixes import tiling drift + vertical squash). Optional for backward
   * compatibility: absent ⇒ the importer falls back to the whole canvas.
   */
  unitRect?: [number, number, number, number];
}

const ARTIST_TILES = new Set<ArtistTile>(['standard', 'hexagon', 'rhombus', 'parallelogram']);

/** Conservative per-QR character budget; longer payloads chain across codes.
 *  Well below a single QR's capacity at error-correction level M. */
const CHUNK_CHARS = 800;

export function serializeData(d: EscherllateData): string {
  return JSON.stringify(d);
}

/** A QR module matrix (true = dark module), from the DOM-free qrcode builder. */
function qrMatrix(text: string): boolean[][] {
  const qr = createQr(text, { errorCorrectionLevel: 'M' });
  const size = qr.modules.size;
  const m: boolean[][] = [];
  for (let r = 0; r < size; r++) {
    const row: boolean[] = [];
    for (let c = 0; c < size; c++) row.push(qr.modules.get(r, c) === 1);
    m.push(row);
  }
  return m;
}

/** Split a payload across as many QR codes as needed, each chunk tagged
 *  `<i>/<N>|` so the decoder can reassemble regardless of layer order. */
export function encodeToMatrices(payload: string): boolean[][][] {
  const chunks: string[] = [];
  for (let i = 0; i < payload.length; i += CHUNK_CHARS) chunks.push(payload.slice(i, i + CHUNK_CHARS));
  if (chunks.length === 0) chunks.push('');
  const n = chunks.length;
  return chunks.map((ch, idx) => qrMatrix(`${idx + 1}/${n}|${ch}`));
}

/** Render a module matrix to a raw RGBA buffer (black on white) with a quiet
 *  zone — the shared renderer for both the export canvas and decode round-trip
 *  tests. No DOM. */
export function matrixToRGBA(
  matrix: boolean[][],
  modulePx = 4,
  quietModules = 4,
): { data: Uint8ClampedArray; width: number; height: number } {
  const n = matrix.length;
  const dim = (n + quietModules * 2) * modulePx;
  const data = new Uint8ClampedArray(dim * dim * 4);
  data.fill(255); // white, fully opaque
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (!matrix[r]![c]) continue;
      const px0 = (c + quietModules) * modulePx;
      const py0 = (r + quietModules) * modulePx;
      for (let y = 0; y < modulePx; y++) {
        for (let x = 0; x < modulePx; x++) {
          const o = ((py0 + y) * dim + (px0 + x)) * 4;
          data[o] = 0;
          data[o + 1] = 0;
          data[o + 2] = 0;
          // alpha already 255
        }
      }
    }
  }
  return { data, width: dim, height: dim };
}

/** Decode one QR image (raw RGBA) to its chunk string, or null. */
export function decodeImage(img: { data: Uint8ClampedArray; width: number; height: number }): string | null {
  const res = jsQR(img.data, img.width, img.height);
  return res ? res.data : null;
}

/** Decode + reassemble the payload from every data-layer image. Null if any
 *  code fails to decode or the `<i>/<N>|` chunk set is incomplete/inconsistent. */
export function decodeDataImages(images: { data: Uint8ClampedArray; width: number; height: number }[]): string | null {
  if (images.length === 0) return null;
  const parsed: { i: number; n: number; body: string }[] = [];
  for (const img of images) {
    const s = decodeImage(img);
    if (s === null) return null;
    const m = /^(\d+)\/(\d+)\|/.exec(s);
    if (!m) return null;
    parsed.push({ i: Number(m[1]), n: Number(m[2]), body: s.slice(m[0].length) });
  }
  const n = parsed[0]!.n;
  if (parsed.length !== n || parsed.some((p) => p.n !== n)) return null;
  parsed.sort((a, b) => a.i - b.i);
  if (parsed.some((p, idx) => p.i !== idx + 1)) return null;
  return parsed.map((p) => p.body).join('');
}

export type DataValidation = { data: EscherllateData } | { error: string };

/** Parse + validate a decoded payload string. Mirrors psdMetadata's field
 *  checks so an invalid packet reports which field is wrong. */
export function validateData(payload: string): DataValidation {
  let obj: unknown;
  try {
    obj = JSON.parse(payload);
  } catch {
    return { error: 'data is not valid JSON' };
  }
  if (!obj || typeof obj !== 'object') return { error: 'data is not an object' };
  const d = obj as Record<string, unknown>;

  if (d.v !== 1) return { error: `unsupported data version (${String(d.v)})` };
  if (typeof d.tileId !== 'string') return { error: 'tile id is missing' };
  if (typeof d.wpgId !== 'string' || !(d.wpgId in WALLPAPER_GROUPS)) {
    return { error: `unknown wallpaper group (${String(d.wpgId)})` };
  }
  const wpgId = d.wpgId as WallpaperGroupId;
  if (typeof d.artistTile !== 'string' || !ARTIST_TILES.has(d.artistTile as ArtistTile)) {
    return { error: `unknown artist tile (${String(d.artistTile)})` };
  }
  if (typeof d.colourCount !== 'number' || !Number.isInteger(d.colourCount)) {
    return { error: 'colour count is not an integer' };
  }
  if (!colourCountsFor(wpgId).includes(d.colourCount)) {
    return { error: `colour count ${d.colourCount} is not valid for ${wpgId}` };
  }
  if (typeof d.latticeParams !== 'object' || d.latticeParams === null) {
    return { error: 'lattice parameters are missing' };
  }
  if (!Array.isArray(d.regions) || d.regions.some((r) => typeof r !== 'string')) {
    return { error: 'region names are missing or malformed' };
  }
  if (typeof d.canvasW !== 'number' || typeof d.canvasH !== 'number') {
    return { error: 'canvas dimensions are missing' };
  }
  let unitRect: [number, number, number, number] | undefined;
  if (d.unitRect !== undefined) {
    const r = d.unitRect;
    if (!Array.isArray(r) || r.length !== 4 || r.some((n) => typeof n !== 'number' || !Number.isFinite(n))) {
      return { error: 'unit rect is malformed' };
    }
    unitRect = [r[0], r[1], r[2], r[3]] as [number, number, number, number];
  }

  return {
    data: {
      v: 1,
      tileId: d.tileId,
      wpgId,
      latticeParams: d.latticeParams as LatticeParams,
      artistTile: d.artistTile as ArtistTile,
      colourCount: d.colourCount,
      regions: d.regions as string[],
      canvasW: d.canvasW,
      canvasH: d.canvasH,
      unitRect,
    },
  };
}

/**
 * Tile-space rect [x0,y0,x1,y1] covered by the FULL PSD canvas (not just the
 * unit cell), derived from `unitRect` (the pixel rect of tile-space [0,1]²
 * within the canvas) and the canvas's own pixel dimensions. Inverts the
 * unitRect mapping: pixel 0 maps to tile-space `-x0/sx`, pixel `canvasW` maps
 * to `(canvasW-x0)/sx`, etc. Used by the region-render path (psdImport.ts) to
 * place the FULL merged bitmap — including paint outside the unit cell —
 * rather than cropping it away. Degenerate `unitRect` (zero/negative extent)
 * falls back to the unit rect itself, matching the legacy no-crop behaviour.
 */
export function tileRectFromUnitRect(
  unitRect: readonly [number, number, number, number],
  canvasW: number,
  canvasH: number,
): [number, number, number, number] {
  const [ux0, uy0, ux1, uy1] = unitRect;
  const sx = ux1 - ux0;
  const sy = uy1 - uy0;
  if (sx <= 0 || sy <= 0) return [0, 0, 1, 1];
  return [(0 - ux0) / sx, (0 - uy0) / sy, (canvasW - ux0) / sx, (canvasH - uy0) / sy];
}

export type FileCheck = { ok: true } | { error: string };

/** §3 sanity cross-check: does the decoded payload describe THIS file? Fatal
 *  only for a gross mismatch — a canvas-dimension disagreement, or NONE of the
 *  declared regions present at all (a wholly wrong/edited file). A partial
 *  overlap (the artist legitimately removed one region's paint layer) is not
 *  fatal here — it surfaces as the soft `missingRegions` warning downstream. */
export function checkAgainstFile(
  data: EscherllateData,
  file: { paintRegionNames: readonly string[]; width: number; height: number },
): FileCheck {
  if (data.canvasW !== file.width || data.canvasH !== file.height) {
    return {
      error: `embedded data's canvas (${data.canvasW}×${data.canvasH}) doesn't match this file (${file.width}×${file.height})`,
    };
  }
  if (data.regions.length > 0 && !data.regions.some((r) => file.paintRegionNames.includes(r))) {
    return { error: `none of the embedded data's regions (${data.regions.join(', ')}) are present in this file` };
  }
  return { ok: true };
}
